import { useCallback } from 'react'

import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { newReleasedPromoteEvent } from 'src/Helpers/TagManager/CampaignsEvents'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import { useGetNewReleaseQuery } from 'src/Redux/newReleaseApi'
import {
  useCreateSpotifyCampaignMutation,
  useUpdateSpotifyCampaignGenresMutation,
  useUpdateSpotifyCampaignLanguagesMutation,
} from 'src/Redux/spotify-campaignApi'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
} from 'src/Types/campaigns'
import { ScheduleSteps } from 'src/Types/ui'
import { sendSourceBusterEvent } from 'src/Redux/sourcebuster-process/api-actions'
import { getCookieByName, getUtmParameters } from 'src/Constants/functions'
import { ANY_LANGUAGE_ID } from 'src/Constants/constants'
import {
  getAccessToken,
  getIsArtist,
} from 'src/Redux/auth-process/credentialsSlice/selectors'
import {
  campaignsList,
  createCampaign,
  scheduleCampaign,
} from 'src/Router/routes'

import { useCreateCampaignEvents } from './Analytics/useCreateCampaignEvents'

interface CreateAndStartScheduleEvents {
  createAndStartSchedule: (isTop?: boolean) => void
  loading: boolean
}

export const useCreateAndStartScheduleNewRelease =
  (): CreateAndStartScheduleEvents => {
    const dispatch = useAppDispatch()
    const token = useAppSelector(getAccessToken)
    const userId = useAppSelector(getUserId)
    const isArtist = useSelector(getIsArtist)
    const navigate = useNavigate()
    const { createCampaignEventHandler, createdSpotifyCampaignEventHandler } =
      useCreateCampaignEvents()
    const [createSpotifyCampaign, { isLoading }] =
      useCreateSpotifyCampaignMutation()
    const [updateSpotifyCampaignGenres] =
      useUpdateSpotifyCampaignGenresMutation()
    const [updateSpotifyCampaignLanguages] =
      useUpdateSpotifyCampaignLanguagesMutation()
    const { data, refetch } = useGetNewReleaseQuery(null, {
      skip: !isArtist,
    })
    const trackSpotifyId = data?.trackSpotifyId
    const trackName = data?.trackName
    const trackImageUrl = data?.trackImageUrl
    const trackDuration = data?.trackDuration
    const artistName = data?.artistName
    const artistSpotifyId = data?.artistSpotifyId
    const sendSourceBusterData = useCallback(
      (id: number, campaignPlatform: CampaignPlatformTypeEnum): void => {
        const utmObj = getUtmParameters(getCookieByName('soundCampaignUtm'))

        void dispatch(
          sendSourceBusterEvent({
            token,
            campaignPlatform,
            campaignId: id,
            eventName: 'create_basic_campaign',
            gclid: utmObj.gclid ?? '',
          }),
        )
      },
      [dispatch, token],
    )

    const createAndStartSchedule = useCallback(
      (isTop?: boolean) => {
        if (trackSpotifyId) {
          createSpotifyCampaign({
            trackName: trackName ?? '',
            trackImage: trackImageUrl ?? '',
            trackLength: trackDuration ?? 0,
            artistName: artistName ?? '',
            artistSpotifyId: artistSpotifyId ?? '',
            trackUrl: `https://api.spotify.com/v1/tracks/${trackSpotifyId}`,
            artistUrl: `https://open.spotify.com/artist/${artistSpotifyId}`,
            trackSpotifyId,
          })
            .unwrap()
            .then((response) => {
              void updateSpotifyCampaignGenres({
                id: String(response.id),
                genreIds: [],
              })
              void updateSpotifyCampaignLanguages({
                id: String(response.id),
                languageIds: [ANY_LANGUAGE_ID],
              })

              createCampaignEventHandler({ campaignId: response?.id })

              createdSpotifyCampaignEventHandler({
                campaignId: response?.id,
                spotifyTrackId: response?.track?.id,
              })

              sendSourceBusterData(
                response?.id,
                CampaignPlatformTypeEnum.SPOTIFY,
              )
              isTop &&
                newReleasedPromoteEvent({
                  userId,
                  trackId: trackSpotifyId,
                })
              refetch().catch(() => {})
              navigate(
                response.status === CampaignStatusEnum.APPROVED
                  ? `${campaignsList}${scheduleCampaign}/${response.id}?step=${ScheduleSteps.SETTINGS}`
                  : `${campaignsList}${createCampaign}/${CampaignPlatformTypeEnum.SPOTIFY}?successCreatedCampaign=true`,
              )
            })
        }
      },
      [
        createCampaignEventHandler,
        createSpotifyCampaign,
        createdSpotifyCampaignEventHandler,
        updateSpotifyCampaignGenres,
        updateSpotifyCampaignLanguages,
        artistName,
        artistSpotifyId,
        trackDuration,
        trackImageUrl,
        trackName,
        trackSpotifyId,
        navigate,
        refetch,
        sendSourceBusterData,
        userId,
      ],
    )

    return { createAndStartSchedule, loading: isLoading }
  }
